import React, { FC } from "react";
import { useAuthContext } from "@dewo/app/contexts/AuthContext";
import { Button, Col, Dropdown, Menu, Modal, Row, Typography } from "antd";
import Link from "next/link";
import { UserAvatar } from "@dewo/app/components/avatars/UserAvatar";
import {
  useCopyToClipboardAndShowToast,
  useToggle,
} from "@dewo/app/util/hooks";
import { UserSettings } from "../user/UserSettings";
import { ThreepidSource } from "@dewo/app/graphql/types";
import { shortenedAddress } from "../payment/hooks";
import { LogoutOutlined, SettingOutlined } from "@ant-design/icons";
import { NotificationPopoverButton } from "../notification/NotificationPopoverButton";

export const UserWalletAddress: FC<{ onShowSettings: () => void }> = ({
  onShowSettings,
}) => {
  const { user } = useAuthContext();
  const discordConnected = user?.threepids.some(
    (t) => t.source === ThreepidSource.discord
  );
  const ethWalletAddress = user?.threepids.find(
    (t) => t.source === ThreepidSource.metamask
  );
  const copy = useCopyToClipboardAndShowToast(
    "Copied wallet address to clipboard"
  );
  if (discordConnected && ethWalletAddress) {
    return (
      <Typography.Text
        onClick={() => copy(ethWalletAddress.threepid)}
        type="secondary"
        className="ant-typography-caption"
        children={shortenedAddress(ethWalletAddress.threepid)}
      />
    );
  }
  return (
    <Typography.Link
      className="ant-typography-caption"
      onClick={onShowSettings}
    >
      {discordConnected ? "Connect wallet" : "Connect Discord"}
    </Typography.Link>
  );
};

export const MenuFooter: FC = () => {
  const { user, logout } = useAuthContext();
  const userSettings = useToggle();

  if (!user) return null;
  return (
    <Row
      style={{
        paddingRight: 4,
        paddingLeft: 4,
        paddingBottom: 8,
        paddingTop: 8,
      }}
    >
      <Row gutter={4} align="middle" className="w-full pl-2">
        <Col>
          <UserAvatar user={user} linkToProfile />
        </Col>
        <Col style={{ flex: 1, overflow: "hidden" }}>
          <Row>
            <Link href={user?.permalink ?? ""}>
              <a href={user?.permalink} style={{ width: "100%" }}>
                <Typography.Text ellipsis className="font-semibold">
                  {user.username}
                </Typography.Text>
              </a>
            </Link>
          </Row>
          <Row>
            <UserWalletAddress onShowSettings={userSettings.toggleOn} />
          </Row>
        </Col>
        <Col style={{ display: "flex" }}>
          <Dropdown
            trigger={["click"]}
            placement="topRight"
            overlay={
              <Menu>
                <Menu.Item
                  key="settings"
                  icon={<SettingOutlined />}
                  onClick={userSettings.toggleOn}
                >
                  Settings
                </Menu.Item>
                <Menu.Item
                  key="logout"
                  icon={<LogoutOutlined />}
                  onClick={() => logout()}
                >
                  Log out
                </Menu.Item>
              </Menu>
            }
          >
            <Button
              type="text"
              className="text-secondary"
              icon={<SettingOutlined />}
            />
          </Dropdown>
          <NotificationPopoverButton />
        </Col>
      </Row>
      <Modal
        visible={userSettings.isOn}
        title="Settings"
        footer={null}
        onCancel={userSettings.toggleOff}
      >
        <UserSettings />
      </Modal>
    </Row>
  );
};
